/**
 * In-app product feedback.
 *
 * Screenshots are sent base64 in the request body and uploaded to SLACK by the proxy, into the
 * report's own thread. They deliberately do NOT go to Firebase Storage: the project-level Storage
 * rules for `finn-agentspace` are owned and deployed by the spaces repo and contain no
 * `people_space/` block, so that write is denied outright (this repo's `storage.rules` is not
 * wired to any deploy target). A Slack file also sits behind channel membership, whereas a
 * `getDownloadURL()` link is unguessable but open to anyone holding it — the better boundary for
 * an image that may show candidate data.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

export const FEEDBACK_TYPES = ['bug', 'feature', 'improvement', 'other'] as const;
export const FEEDBACK_PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;

export type FeedbackType = (typeof FEEDBACK_TYPES)[number];
export type FeedbackPriority = (typeof FEEDBACK_PRIORITIES)[number];

/**
 * 3MB, matching the proxy. Base64 inflates by ~33%, so three 5MB images would blow the proxy's
 * 20MB JSON limit and 413 at the very end of writing a bug report.
 */
export const MAX_SCREENSHOT_BYTES = 3 * 1024 * 1024;
export const MAX_SCREENSHOTS = 3;

export interface FeedbackScreenshot {
  filename: string;
  mimeType: string;
  /** Raw base64, no `data:` prefix. */
  dataBase64: string;
}

/**
 * The Candidate Request a report is about. Candidate data — carried only because
 * #people_spaces_feedback is PRIVATE (Aug 14 2026) and a bad draft cannot be judged without the
 * question that produced it. If that channel is ever public again this has to go.
 */
export interface FeedbackRequestContext {
  candidateName: string;
  candidateEmail: string;
  question: string;
  draft: string;
  draftSource: string;
  categoryLabel: string;
  leverUrl: string;
  gmailUrl: string;
}

export interface FeedbackRequest {
  type: FeedbackType;
  priority: FeedbackPriority;
  title: string;
  /** The three answers the proxy posts as one labelled description (lib/feedbackMessage.js). */
  problem: string;
  expected: string;
  example: string;
  /**
   * The same three answers as plain text. The proxy ignores it when the parts are there; it only
   * keeps a report from failing while the frontend is live and the proxy still runs the old build
   * (deploy.yml ships both jobs in parallel).
   */
  description: string;
  /** Human label of the page, e.g. "TA Action Boards › Requests". */
  surface: string;
  surfaceId: string;
  path: string;
  screenshots: FeedbackScreenshot[];
  /** Only ever set from the Candidate Requests tab, and only when one is picked. */
  request?: FeedbackRequestContext | null;
}

export interface FeedbackResult {
  ok: boolean;
  screenshots?: { sent: number; shared: number };
}

/** FileReader gives us a data URL; Slack wants the payload after the comma. */
export function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.onload = () => {
      const result = `${reader.result ?? ''}`;
      const comma = result.indexOf(',');
      if (comma === -1) {
        reject(new Error(`Could not read ${file.name}.`));
        return;
      }
      resolve(result.slice(comma + 1));
    };
    reader.readAsDataURL(file);
  });
}

export async function toFeedbackScreenshot(file: File): Promise<FeedbackScreenshot> {
  if (!file.type.startsWith('image/')) {
    throw new Error(`${file.name} is not an image — screenshots only.`);
  }
  if (file.size > MAX_SCREENSHOT_BYTES) {
    throw new Error(
      `${file.name} is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 3 MB.`
    );
  }
  return { filename: file.name, mimeType: file.type, dataBase64: await readAsBase64(file) };
}

export async function submitFeedback(body: FeedbackRequest): Promise<FeedbackResult> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return (await res.json()) as FeedbackResult;
}
